import { useRef, useEffect } from 'react'

function getLineColor(): string {
  const isDark = document.documentElement.dataset.theme === 'dark' ||
    (!document.documentElement.dataset.theme &&
      window.matchMedia('(prefers-color-scheme: dark)').matches)
  return isDark ? '255, 255, 255' : '50, 50, 50'
}

interface Props {
  progress: number
}

// Golden/Fibonacci spiral: r = a * phi^(2θ/π)
// where phi = golden ratio. This produces the smooth curve that
// approximates the Fibonacci rectangle construction.
function buildFibonacciSpiral(maxRadius: number, centerX: number, centerY: number) {
  const phi = (1 + Math.sqrt(5)) / 2
  const totalPoints = 600
  const minRadius = 3
  const turns = 4
  const maxTheta = turns * 2 * Math.PI
  const b = Math.log(phi) * 2 / Math.PI
  const points: Array<{ x: number; y: number }> = []

  for (let i = 0; i <= totalPoints; i++) {
    const theta = (i / totalPoints) * maxTheta
    const r = minRadius * Math.exp(b * theta)
    if (r > maxRadius) break
    points.push({
      x: centerX + r * Math.cos(theta),
      y: centerY - r * Math.sin(theta),
    })
  }

  return points
}

export function FibonacciCurve({ progress }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const progressRef = useRef(progress)
  progressRef.current = progress

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const rect = canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    ctx.scale(dpr, dpr)
    const width = rect.width
    const height = rect.height

    const maxRadius = Math.min(width, height) * 0.25
    const spiralPoints = buildFibonacciSpiral(maxRadius, width / 2, height / 2)

    let animationId: number
    let lastFrameTime = 0
    const frameInterval = 1000 / 30
    let dotPulseTime = 0

    function animate(currentTime: number) {
      if (!lastFrameTime) lastFrameTime = currentTime
      const delta = currentTime - lastFrameTime

      if (delta >= frameInterval) {
        ctx!.clearRect(0, 0, width, height)
        dotPulseTime += 0.03

        const p = progressRef.current
        const rgb = getLineColor()
        const visiblePoints = Math.floor(p * spiralPoints.length)

        if (visiblePoints > 1) {
          for (let i = 1; i < visiblePoints; i++) {
            const p1 = spiralPoints[i - 1]
            const p2 = spiralPoints[i]

            const age = i / visiblePoints
            const fadeIn = Math.min(1, (visiblePoints - i) / 40)
            const alpha = (0.1 + age * 0.7) * fadeIn

            ctx!.beginPath()
            ctx!.moveTo(p1.x, p1.y)
            ctx!.lineTo(p2.x, p2.y)
            ctx!.strokeStyle = `rgba(${rgb}, ${alpha})`
            ctx!.lineWidth = 0.6 + age * 0.6
            ctx!.stroke()
          }
        }

        // Pulsing dot at tip
        const beat = Math.pow(Math.sin(dotPulseTime), 2)
        const dotAlpha = 0.5 + beat * 0.5
        const dotSize = 2 + beat * 1.5

        const tipIdx = Math.max(0, Math.min(visiblePoints, spiralPoints.length - 1))
        const tip = spiralPoints[tipIdx]

        for (let g = 3; g >= 1; g--) {
          ctx!.beginPath()
          ctx!.fillStyle = `rgba(${rgb}, ${dotAlpha * 0.04 / g})`
          ctx!.arc(tip.x, tip.y, dotSize * g * 2.5, 0, Math.PI * 2)
          ctx!.fill()
        }

        ctx!.beginPath()
        ctx!.fillStyle = `rgba(${rgb}, ${dotAlpha})`
        ctx!.arc(tip.x, tip.y, dotSize, 0, Math.PI * 2)
        ctx!.fill()

        lastFrameTime = currentTime - (delta % frameInterval)
      }

      animationId = requestAnimationFrame(animate)
    }

    animationId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationId)
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full"
      style={{
        maskImage: 'radial-gradient(circle, black 20%, transparent 60%)',
        WebkitMaskImage: 'radial-gradient(circle, black 20%, transparent 60%)',
      }}
    />
  )
}
